import * as React from 'react';
import * as RadixDialog from '@radix-ui/react-dialog';
import { cn } from '../../lib/cn';

/**
 * Progressive viewport-height enhancement for the scrollable body: `vh` is
 * the universal fallback, `svh` ("small viewport height", stable even when
 * a mobile browser's chrome/keyboard is showing) overrides it where
 * supported, and `dvh` ("dynamic viewport height", live-tracks chrome
 * show/hide) overrides that where supported.
 *
 * This is a real `<style>` tag rather than three `max-h-[...]` Tailwind
 * classes so the fallback order is guaranteed by literal CSS source order
 * -- Tailwind's build-time utility sort is not guaranteed to preserve the
 * relative order of arbitrary-value classes sharing one property across a
 * whole-project scan, which would silently break the fallback. Same
 * technique (and same reasoning) as the inline `<style>` keyframes block in
 * `packages/jumpstone-ui-react/src/components/drawer/drawer.tsx`.
 */
const DIALOG_SCROLL_BODY_STYLE = `
  .jmp-dialog-scroll-body {
    max-height: 90vh;
    max-height: 90svh;
    max-height: 90dvh;
  }
`;

/**
 * Modal dialog built on Radix primitives. Includes focus trap, escape-key
 * dismiss, click-outside-to-close, and `aria-*` wiring out of the box.
 *
 * Visual treatment matches the glass overlay pattern in the design HTML:
 * blurred backdrop + opaque centered card.
 */
export const Dialog = RadixDialog.Root;
export const DialogTrigger = RadixDialog.Trigger;
export const DialogClose = RadixDialog.Close;
export const DialogPortal = RadixDialog.Portal;

export const DialogOverlay = React.forwardRef<
  React.ComponentRef<typeof RadixDialog.Overlay>,
  React.ComponentPropsWithoutRef<typeof RadixDialog.Overlay>
>(({ className, ...props }, ref) => (
  <RadixDialog.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-ink/60 backdrop-blur-sm',
      'data-[state=open]:animate-in data-[state=closed]:animate-out',
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = 'DialogOverlay';

export const DialogContent = React.forwardRef<
  React.ComponentRef<typeof RadixDialog.Content>,
  React.ComponentPropsWithoutRef<typeof RadixDialog.Content>
>(({ className, children, ...props }, ref) => (
  <RadixDialog.Portal>
    <DialogOverlay />
    <RadixDialog.Content
      ref={ref}
      className={cn(
        'fixed inset-0 z-50 m-auto h-fit',
        // Below 512px (phones), leave a margin on both sides instead of
        // touching the screen edges. `w-full` alone is untouched at >=512px.
        'w-full max-[511px]:w-[calc(100vw-2rem)] max-w-lg rounded-2xl border bg-[var(--color-bg-card)] overflow-hidden',
        'focus:outline-none',
        className,
      )}
      style={{
        borderColor: 'rgba(15,23,42,0.06)',
        borderRadius: '16px',
        boxShadow:
          '0 32px 80px -24px rgba(15,23,42,0.30),0 8px 24px -8px rgba(15,23,42,0.12)',
        overflow: 'hidden',
      }}
      {...props}
    >
      <style>{DIALOG_SCROLL_BODY_STYLE}</style>
      <div className="jmp-dialog-scroll-body overflow-y-auto p-6">
        {children}
      </div>
    </RadixDialog.Content>
  </RadixDialog.Portal>
));
DialogContent.displayName = 'DialogContent';

export const DialogTitle = React.forwardRef<
  React.ComponentRef<typeof RadixDialog.Title>,
  React.ComponentPropsWithoutRef<typeof RadixDialog.Title>
>(({ className, ...props }, ref) => (
  <RadixDialog.Title
    ref={ref}
    className={cn(
      'text-[18px] font-semibold tracking-tight text-ink',
      className,
    )}
    {...props}
  />
));
DialogTitle.displayName = 'DialogTitle';

export const DialogDescription = React.forwardRef<
  React.ComponentRef<typeof RadixDialog.Description>,
  React.ComponentPropsWithoutRef<typeof RadixDialog.Description>
>(({ className, ...props }, ref) => (
  <RadixDialog.Description
    ref={ref}
    className={cn(
      'mt-2 text-[13.5px] text-ink-muted leading-relaxed',
      className,
    )}
    {...props}
  />
));
DialogDescription.displayName = 'DialogDescription';

export function DialogHeader({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'flex flex-col space-y-1.5 text-center sm:text-start',
        className,
      )}
      {...props}
    />
  );
}

export function DialogFooter({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        '-mx-6 -mb-6 mt-6 flex items-center justify-end gap-3 rounded-b-[16px] px-6 pt-4',
        // `pb-[max(1rem,env(safe-area-inset-bottom))]` replaces the
        // shorthand `py-4`'s bottom half so the footer respects the iOS
        // home-indicator safe area. `env()` resolves to 0 without one, so
        // this is `1rem` -- identical to the old `py-4` -- everywhere else.
        'pb-[max(1rem,env(safe-area-inset-bottom))]',
        className,
      )}
      style={{ background: 'rgba(241,245,249,0.6)' }}
      {...props}
    />
  );
}
